import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CLINIC } from "@/lib/site";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/site/LanguageSelector";

export const Navbar = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.home, href: "#hero" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.conditions, href: "#conditions" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contact, href: "#contact" },
  ];

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-background/90 backdrop-blur-xl border-b border-border shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="container-x flex items-center justify-between h-16 md:h-20">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="flex items-center gap-3 group text-left"
        >
          <img
            src={CLINIC.logo}
            alt="FizioFit Center Logo"
            className="h-10 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="flex flex-col">
            <span className="font-extrabold tracking-tight text-lg sm:text-xl text-foreground leading-tight">
              FizioFit<span className="text-accent font-bold"> Center</span>
            </span>
            <span className="text-[11px] text-muted-foreground font-bold tracking-wider uppercase hidden sm:block">
              {t.hero.specializedCenter}
            </span>
          </div>
        </button>

        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="text-[15px] font-bold text-foreground/80 hover:text-accent transition-colors link-underline"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5 sm:gap-3">
          {/* Language Selector */}
          <LanguageSelector />

          <button
            data-testid="nav-cta-booking"
            onClick={() => go("#booking")}
            className="hidden sm:inline-flex items-center h-10 md:h-11 px-5 md:px-6 rounded-sm bg-accent text-white text-sm font-bold hover:bg-accent/90 shadow-md shadow-accent/25 transition-all duration-300 cursor-pointer"
          >
            {t.nav.bookBtn}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-sm border-2 border-border text-foreground hover:border-accent transition-colors cursor-pointer"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-background border-b border-border shadow-lg">
          <div className="container-x py-6 flex flex-col gap-1">
            {navLinks.map((l) => (
              <button
                key={l.href}
                data-testid={`mobile-link-${l.href.slice(1)}`}
                onClick={() => go(l.href)}
                className="text-left py-3 text-base text-foreground border-b border-border/60 last:border-0 hover:text-accent transition-colors"
              >
                {l.label}
              </button>
            ))}

            <LanguageSelector mobile />

            <button
              data-testid="mobile-cta-booking"
              onClick={() => go("#booking")}
              className="mt-4 inline-flex items-center justify-center h-12 rounded-sm bg-accent text-white text-sm font-medium hover:bg-accent/90 shadow-md shadow-accent/25"
            >
              {t.nav.bookBtn}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
